import { ref } from 'vue';

const STORAGE_KEY = 'pwa_deliverer_token';
const ASKED_KEY = 'push_permission_asked_deliverer';

export type PushPermissionState = 'unsupported' | 'default' | 'granted' | 'denied';

export const pushPermission = ref<PushPermissionState>(
    typeof window === 'undefined' || !('Notification' in window)
        ? 'unsupported'
        : (Notification.permission as PushPermissionState),
);

function urlBase64ToUint8Array(base64: string): Uint8Array {
    const padding = '='.repeat((4 - (base64.length % 4)) % 4);
    const b64 = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
    const raw = atob(b64);
    return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

function isSupported(): boolean {
    return typeof window !== 'undefined'
        && 'PushManager' in window
        && 'serviceWorker' in navigator
        && 'Notification' in window;
}

async function saveSubscription(sub: PushSubscription): Promise<void> {
    const token = localStorage.getItem(STORAGE_KEY);
    if (!token) return;

    const key = sub.getKey('p256dh');
    const auth = sub.getKey('auth');
    if (!key || !auth) return;

    await fetch('/api/v1/push/subscribe', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
            endpoint: sub.endpoint,
            public_key: btoa(String.fromCharCode(...new Uint8Array(key))),
            auth_token: btoa(String.fromCharCode(...new Uint8Array(auth))),
        }),
    });
}

async function subscribe(): Promise<void> {
    const vapidKey = import.meta.env.VITE_VAPID_PUBLIC_KEY as string | undefined;
    if (!vapidKey) return;

    const reg = await navigator.serviceWorker.ready;
    let sub = await reg.pushManager.getSubscription();

    if (!sub) {
        sub = await reg.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(vapidKey),
        });
    }

    await saveSubscription(sub);
}

/** Chamado automaticamente no login — pede permissão só na primeira vez. */
export async function initPushNotifications(): Promise<void> {
    if (!isSupported()) return;

    if (localStorage.getItem(ASKED_KEY)) {
        if (Notification.permission === 'granted') {
            await subscribe().catch(() => {});
        }
        return;
    }

    localStorage.setItem(ASKED_KEY, '1');

    const permission = await Notification.requestPermission();
    pushPermission.value = permission as PushPermissionState;
    if (permission === 'granted') {
        await subscribe().catch(() => {});
    }
}

/** Chamado pelo botão manual na tela Conta — sempre solicita/reativa. */
export async function requestPushPermission(): Promise<void> {
    if (!isSupported()) return;

    localStorage.setItem(ASKED_KEY, '1');

    const permission = await Notification.requestPermission();
    pushPermission.value = permission as PushPermissionState;
    if (permission === 'granted') {
        await subscribe().catch(() => {});
    }
}
